import { Button } from '@heidi/design-system/button';
import {
  TypographyCaption,
  TypographyFootNoteBold,
  TypographyP2SemiBold,
} from '@heidi/design-system/typography';
import { cn } from '@heidi/utils';
import Image from 'next/image';
import type { ReactElement } from 'react';
import { FormattedMessage } from 'react-intl';
import newInReleaseIllustration from './assets/new-in-release-illustration.png';

import { newInThisReleaseCardMessages } from './messages';

export type NewInThisReleaseCardProps = {
  /** Secondary CTA — opens the dictation help page. */
  onLearnMore: () => void;
  /** Primary CTA ("Ok got it") — dismisses the card. */
  onDismiss: () => void;
  className?: string;
};

const bulletMessages = [
  newInThisReleaseCardMessages.bulletTone,
  newInThisReleaseCardMessages.bulletVerbalCommands,
  newInThisReleaseCardMessages.bulletSnippets,
];

/**
 * Awareness card surfaced to existing users after an app update, highlighting
 * the latest dictation capabilities. Presentational only — the consumer owns
 * visibility, modal-queue wiring, and "seen" persistence.
 */
export function NewInThisReleaseCard({
  onLearnMore,
  onDismiss,
  className,
}: NewInThisReleaseCardProps): ReactElement {
  return (
    <div
      className={cn(
        'flex w-full max-w-[280px] flex-col overflow-hidden rounded-xl bg-surface-container shadow-md',
        className
      )}
    >
      {/* Decorative raster illustration; copy below carries the meaning. */}
      <Image
        src={newInReleaseIllustration}
        alt=""
        aria-hidden
        className="h-auto w-full select-none"
      />
      <div className="flex flex-col gap-4 px-4 pb-4 pt-2">
        <div className="flex flex-col gap-1">
          <TypographyP2SemiBold className="text-text-primary">
            <FormattedMessage {...newInThisReleaseCardMessages.title} />
          </TypographyP2SemiBold>
          <TypographyFootNoteBold className="text-text-secondary">
            <FormattedMessage {...newInThisReleaseCardMessages.subtitle} />
          </TypographyFootNoteBold>
          <ul className="flex list-disc flex-col gap-0.5 ps-4 text-text-secondary">
            {bulletMessages.map((message) => (
              <li key={message.id}>
                <TypographyCaption className="text-text-secondary">
                  <FormattedMessage {...message} />
                </TypographyCaption>
              </li>
            ))}
          </ul>
        </div>
        <div className="flex items-center justify-end gap-2">
          <Button type="button" variant="ghost" size="xs" onClick={onLearnMore}>
            <FormattedMessage {...newInThisReleaseCardMessages.learnMore} />
          </Button>
          <Button type="button" size="xs" onClick={onDismiss}>
            <FormattedMessage {...newInThisReleaseCardMessages.dismiss} />
          </Button>
        </div>
      </div>
    </div>
  );
}
